import { create } from 'zustand';

import { useAuthStore } from '../auth/store';

/*
 * Unfinished resumable uploads, remembered in this browser (docs/modules/files.md "Resumable uploads"). A page reload
 * loses the `File` object but not the upload on the server, so what is kept here is what is needed to continue it once the
 * user picks the same file again: the session id and a fingerprint of the file. Strictly a convenience — without storage
 * (private window, blocked site data) every upload simply starts from the beginning.
 */

const STORAGE_KEY = 'orbita.resumable-uploads.v1';
/** More than a user can have open at the server (Files:Upload:Resumable:MaxPendingSessionsPerUser) so the list stays small. */
const MAX_ENTRIES = 50;

export interface PendingUpload {
  /** `name|size|lastModified`: what "the same file picked again" means. */
  fingerprint: string;
  /** Whose upload it is: another user of this browser neither sees nor continues it. */
  userId: string;
  sessionId: string;
  name: string;
  size: number;
  lastModified: number;
  contentType: string;
  /** Bytes the server had stored when we last heard from it. */
  offset: number;
  /** When the session ends at the server (epoch milliseconds). */
  expiresAt: number;
  savedAt: number;
  /** What the module that started the upload needs to continue it after a reload (e.g. the Disk folder). */
  meta?: Record<string, unknown>;
}

/** What identifies a file across page loads. */
export function fingerprintOf(file: Pick<File, 'name' | 'size' | 'lastModified'>): string {
  return `${file.name}|${file.size}|${file.lastModified}`;
}

function currentUserId(): string | null {
  return useAuthStore.getState().user?.id ?? null;
}

function isEntry(value: unknown): value is PendingUpload {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.fingerprint === 'string' &&
    typeof v.userId === 'string' &&
    typeof v.sessionId === 'string' &&
    typeof v.name === 'string' &&
    typeof v.size === 'number' &&
    typeof v.lastModified === 'number' &&
    typeof v.offset === 'number' &&
    typeof v.expiresAt === 'number'
  );
}

function readAll(): PendingUpload[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isEntry) : [];
  } catch {
    return [];
  }
}

function writeAll(entries: PendingUpload[]): void {
  try {
    const kept = entries.sort((a, b) => b.savedAt - a.savedAt).slice(0, MAX_ENTRIES);
    if (kept.length === 0) window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, JSON.stringify(kept));
  } catch {
    // Storage is full or blocked: the upload just cannot be continued after a reload.
  }
}

/** The signed-in user's unfinished uploads whose sessions have not ended. */
export function listPendingUploads(now = Date.now()): PendingUpload[] {
  const userId = currentUserId();
  if (!userId) return [];
  return readAll().filter((entry) => entry.userId === userId && entry.expiresAt > now);
}

export function findPendingUpload(fingerprint: string, now = Date.now()): PendingUpload | undefined {
  return listPendingUploads(now).find((entry) => entry.fingerprint === fingerprint);
}

/** Keeps (or updates) the record of an unfinished upload. */
export function rememberPendingUpload(entry: Omit<PendingUpload, 'userId' | 'savedAt'>): void {
  const userId = currentUserId();
  if (!userId) return;
  const others = readAll().filter((e) => !(e.userId === userId && e.fingerprint === entry.fingerprint));
  writeAll([...others, { ...entry, userId, savedAt: Date.now() }]);
  usePendingUploads.getState().refresh();
}

/** Forgets an upload that finished, was cancelled or can no longer be continued. */
export function forgetPendingUpload(fingerprint: string): void {
  const userId = currentUserId();
  const all = readAll();
  const rest = all.filter((e) => !(e.userId === userId && e.fingerprint === fingerprint));
  if (rest.length !== all.length) writeAll(rest);
  usePendingUploads.getState().refresh();
}

/** Drops entries nobody can continue any more (ended sessions) from storage. */
export function pruneExpiredPendingUploads(now = Date.now()): void {
  const all = readAll();
  const live = all.filter((e) => e.expiresAt > now);
  if (live.length !== all.length) writeAll(live);
}

interface PendingUploadsState {
  /** The signed-in user's unfinished uploads (the Disk panel lists them after a reload). */
  entries: PendingUpload[];
  refresh: () => void;
}

/** The unfinished uploads as state, so that panels follow changes made here and in other tabs. */
export const usePendingUploads = create<PendingUploadsState>()((set) => ({
  entries: [],
  refresh: () => set({ entries: listPendingUploads() }),
}));

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY || event.key === null) usePendingUploads.getState().refresh();
  });
}
